import * as React from 'react';

import CoursePage from '../CoursePage'
import { Tooltip } from '../../../Shared/index'
import { teksPremiumBanner } from '../../helpers/teks';
import {
  EIGHTH_GRADE_TEKS_ACTIVITY_PACKS,
  ENGLISH_I_TEKS_ACTIVITY_PACKS,
  ENGLISH_II_TEKS_ACTIVITY_PACKS,
  FIFTH_GRADE_TEKS_ACTIVITY_PACKS,
  FOURTH_GRADE_TEKS_ACTIVITY_PACKS,
  SEVENTH_GRADE_TEKS_ACTIVITY_PACKS,
  SIXTH_GRADE_TEKS_ACTIVITY_PACKS,
  TEKS_COURSE_PAGE,
  TEKS_IMAGE_CREDIT_TOOLTIP_TEXT,
  THIRD_GRADE_TEKS_ACTIVITY_PACKS,
  chevronLeftImgSrc,
  teksHeaderImgSrc,
} from '../../constants/contentHubs';
import { useTrackChildPageVisit } from '../../hooks/useTrackChildPageVisit';

export const GRADE_CONFIGS = {
  'third-grade': {
    localStorageKey: 'teksThirdGradeExpandedUnitTemplateIds',
    title: THIRD_GRADE_TEKS_ACTIVITY_PACKS,
    description: 'Support rigorous writing and grammar instruction for Texas third graders with TEKS-aligned activity packs. Each activity is designed to reinforce a specific third-grade TEKS ELAR standard through short, evidence-based practice. Students connect with engaging Texas topics while mastering key grammar and writing skills.',
    unitTemplatePath: '/teachers/progress_reports/teks_third_grade_unit_templates',
    gradeName: 'Third Grade',
  },
  'fourth-grade': {
    localStorageKey: 'teksFourthGradeExpandedUnitTemplateIds',
    title: FOURTH_GRADE_TEKS_ACTIVITY_PACKS,
    description: 'Support rigorous writing and grammar instruction for Texas fourth graders with TEKS-aligned activity packs. Each activity is designed to reinforce a specific fourth-grade TEKS ELAR standard through short, evidence-based practice. Students connect with engaging Texas topics while mastering key grammar and writing skills.',
    unitTemplatePath: '/teachers/progress_reports/teks_fourth_grade_unit_templates',
    gradeName: 'Fourth Grade',
  },
  'fifth-grade': {
    localStorageKey: 'teksFifthGradeExpandedUnitTemplateIds',
    title: FIFTH_GRADE_TEKS_ACTIVITY_PACKS,
    description: 'Support rigorous writing and grammar instruction for Texas fifth graders with TEKS-aligned activity packs. Each activity is designed to reinforce a specific fifth-grade TEKS ELAR standard through short, evidence-based practice. Students connect with engaging Texas topics while mastering key grammar and writing skills.',
    unitTemplatePath: '/teachers/progress_reports/teks_fifth_grade_unit_templates',
    gradeName: 'Fifth Grade',
  },
  'sixth-grade': {
    localStorageKey: 'teksSixthGradeExpandedUnitTemplateIds',
    title: SIXTH_GRADE_TEKS_ACTIVITY_PACKS,
    description: 'Support rigorous writing and grammar instruction for Texas sixth graders with TEKS-aligned activity packs. Each activity is designed to reinforce a specific sixth-grade TEKS ELAR standard through short, evidence-based practice. Students connect with engaging Texas topics while mastering key grammar and writing skills.',
    unitTemplatePath: '/teachers/progress_reports/teks_sixth_grade_unit_templates',
    gradeName: 'Sixth Grade',
  },
  'seventh-grade': {
    localStorageKey: 'teksSeventhGradeExpandedUnitTemplateIds',
    title: SEVENTH_GRADE_TEKS_ACTIVITY_PACKS,
    description: 'Support rigorous writing and grammar instruction for Texas seventh graders with TEKS-aligned activity packs. Each activity is designed to reinforce a specific seventh-grade TEKS ELAR standard through short, evidence-based practice. Students connect with engaging Texas topics while mastering key grammar and writing skills.',
    unitTemplatePath: '/teachers/progress_reports/teks_seventh_grade_unit_templates',
    gradeName: 'Seventh Grade',
  },
  'eighth-grade': {
    localStorageKey: 'teksEighthGradeExpandedUnitTemplateIds',
    title: EIGHTH_GRADE_TEKS_ACTIVITY_PACKS,
    description: 'Support rigorous writing and grammar instruction for Texas eighth graders with TEKS-aligned activity packs. Each activity is designed to reinforce a specific eighth-grade TEKS ELAR standard through short, evidence-based practice. Students connect with engaging Texas topics while mastering key grammar and writing skills.',
    unitTemplatePath: '/teachers/progress_reports/teks_eighth_grade_unit_templates',
    gradeName: 'Eighth Grade',
  },
  'english-i': {
    localStorageKey: 'teksEnglishIExpandedUnitTemplateIds',
    title: ENGLISH_I_TEKS_ACTIVITY_PACKS,
    description: 'Support rigorous writing and grammar instruction for Texas English I students with TEKS-aligned activity packs. Each activity is designed to reinforce a specific English I TEKS ELAR standard through short, evidence-based practice. Students connect with engaging Texas topics while mastering key grammar and writing skills.',
    unitTemplatePath: '/teachers/progress_reports/teks_english_i_unit_templates',
    gradeName: 'English I',
  },
  'english-ii': {
    localStorageKey: 'teksEnglishIIExpandedUnitTemplateIds',
    title: ENGLISH_II_TEKS_ACTIVITY_PACKS,
    description: 'Support rigorous writing and grammar instruction for Texas English II students with TEKS-aligned activity packs. Each activity is designed to reinforce a specific English II TEKS ELAR standard through short, evidence-based practice. Students connect with engaging Texas topics while mastering key grammar and writing skills.',
    unitTemplatePath: '/teachers/progress_reports/teks_english_ii_unit_templates',
    gradeName: 'English II',
  },
}

interface TeksGradeLevelProps {
  gradeLevel: string,
  backlinkPath?: string,
  schoolAdmins?: any[],
  subscriptionType?: string
}

const TeksGradeLevel = ({ gradeLevel, backlinkPath, schoolAdmins, subscriptionType }: TeksGradeLevelProps) => {
  useTrackChildPageVisit();

  const config = GRADE_CONFIGS[gradeLevel]
  const contentPage = backlinkPath || window.location.href.split(gradeLevel)[0]
  const contentPageLink = <a className="quill-button medium outlined grey icon focus-on-light" href={contentPage}><img alt="" src={chevronLeftImgSrc} />View all TEKS Activity Packs</a>

  const overviewSection = (
    <div className="overview">
      <div>
        <h1>{config.title}</h1>
        <p className="overview-description">{config.description}</p>
        {teksPremiumBanner(schoolAdmins, subscriptionType)}
      </div>
      <div className="header-image-container">
        <img alt="" src={teksHeaderImgSrc} />
        <Tooltip tooltipText={TEKS_IMAGE_CREDIT_TOOLTIP_TEXT} tooltipTriggerText="Image credit" tooltipTriggerTextClass="image-attribution-tooltip" />
      </div>
    </div>
  )

  return (
    <CoursePage
      activitiesHeader={config.title}
      contentPageLink={contentPageLink}
      courseType={TEKS_COURSE_PAGE}
      localStorageKey={config.localStorageKey}
      overviewSection={overviewSection}
      partnerSection={null}
      subscriptionType={subscriptionType}
      unitTemplatePath={config.unitTemplatePath}
    />
  )
}

export default TeksGradeLevel;
